// ============================================================
// Super Admin Sidebar Navigation
// ============================================================
// Dedicated sidebar for the Super Admin role.

import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Collapse, Typography, Tooltip, Divider } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { tokens } from '@/theme/tokens';
import { superAdminNavigationConfig } from '@/constants/superAdminNavigation';
import { usePermission } from '@/hooks/usePermission';
import { useResponsive } from '@/hooks/useResponsive';
import useThemeStore from '@/store/themeStore';
import useUiStore from '@/store/uiStore';

// ── Sidebar Item ──────────────────────────────────────────
function SidebarItem({ item, collapsed, depth = 0, expandedId, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { can, canAny } = usePermission();

  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.path === location.pathname || (item.activePaths && item.activePaths.some(p => location.pathname.startsWith(p)));
  const isChildActive = hasChildren && item.children.some(
    (child) => child.path === location.pathname || (child.activePaths && child.activePaths.some(p => location.pathname.startsWith(p)))
  );

  const isControlled = depth === 0 && onToggle !== undefined;
  const [localOpen, setLocalOpen] = useState(() => isChildActive);
  const open = isControlled ? expandedId === item.id : localOpen;

  // Filter children by permission
  const visibleChildren = hasChildren
    ? item.children.filter((child) => Array.isArray(child.permission) ? canAny(child.permission) : can(child.permission))
    : [];

  if (hasChildren && visibleChildren.length === 0) return null;

  const handleClick = () => {
    if (hasChildren) {
      if (isControlled) {
        onToggle(item.id);
      } else {
        setLocalOpen(!localOpen);
      }
      if (item.path && item.path !== location.pathname) {
        navigate(item.path);
      }
    } else if (item.path && item.path !== location.pathname) {
      navigate(item.path);
    }
  };

  const Icon = item.icon;

  const button = (
    <ListItemButton
      onClick={handleClick}
      selected={isActive || isChildActive}
      sx={{
        minHeight: 44,
        px: collapsed ? 2.5 : 2,
        py: 0.75,
        mx: 1,
        mb: 0.5,
        borderRadius: tokens.radius.sm,
        justifyContent: collapsed ? 'center' : 'flex-start',
        pl: !collapsed ? 2 + depth * 2 : undefined,
        '&.Mui-selected': {
          backgroundColor: tokens.colors.primary[50],
          color: tokens.colors.primary.main,
          '& .MuiListItemIcon-root': {
            color: tokens.colors.primary.main,
          },
          '&:hover': {
            backgroundColor: tokens.colors.primary[100],
          },
        },
        '&:hover': {
          backgroundColor: tokens.colors.grey[100],
        },
      }}
    >
      {Icon && (
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : (depth > 0 ? 28 : 32),
            justifyContent: 'center',
            color: isActive || isChildActive
              ? tokens.colors.primary.main
              : tokens.colors.text.muted,
          }}
        >
          <Icon size={depth > 0 ? 18 : 20} />
        </ListItemIcon>
      )}
      {!collapsed && (
        <>
          <ListItemText
            primary={item.title}
            slotProps={{
              primary: {
                sx: {
                  fontSize: depth > 0 ? tokens.typography.sizes.sm : tokens.typography.sizes.body,
                  fontWeight: isActive || isChildActive
                    ? tokens.typography.fontWeights.semiBold
                    : tokens.typography.fontWeights.medium,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              },
            }}
            sx={{
              overflow: 'hidden',
              mr: hasChildren || item.badge ? 0.5 : 0,
            }}
          />
          {hasChildren &&
            (open ? <ChevronDown size={16} /> : <ChevronRight size={16} />)}
          {item.badge && (
            <Box
              sx={{
                backgroundColor: tokens.colors.error.main,
                color: '#fff',
                borderRadius: tokens.radius.full,
                fontSize: '11px',
                fontWeight: 700,
                minWidth: 20,
                height: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
              }}
            >
              {item.badge}
            </Box>
          )}
        </>
      )}
    </ListItemButton>
  );

  return (
    <>
      <Tooltip title={item.title} placement="right" arrow disableHoverListener={collapsed ? false : !item.title}>
        {button}
      </Tooltip>

      {/* Nested Children */}
      {hasChildren && !collapsed && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {visibleChildren.map((child) => (
              <SidebarItem key={child.id} item={child} collapsed={false} depth={depth + 1} />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

// ── Super Admin Sidebar Component ─────────────────────────────────────
export function SuperAdminSidebar() {
  const { can, canAny } = usePermission();
  const { isMobileOrTablet } = useResponsive();
  const collapsed = useThemeStore((s) => s.sidebarCollapsed);
  const mobileDrawerOpen = useUiStore((s) => s.mobileDrawerOpen);
  const setMobileDrawerOpen = useUiStore((s) => s.setMobileDrawerOpen);

  const location = useLocation();
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    for (const item of superAdminNavigationConfig) {
      if (item.children && item.children.length > 0) {
        const isChildActive = item.children.some(
          (child) => child.path === location.pathname || (child.activePaths && child.activePaths.some(p => location.pathname.startsWith(p)))
        );
        if (isChildActive) {
          setExpandedId(item.id);
          break;
        }
      }
    }
  }, [location.pathname]);

  const handleToggle = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // Super Admin Navigation Items
  const filteredNav = useMemo(() => {
    return superAdminNavigationConfig;
  }, []);

  const width = collapsed
    ? tokens.layout.sidebar.collapsedWidth
    : tokens.layout.sidebar.expandedWidth;

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: tokens.colors.card,
        borderRight: `1px solid ${tokens.colors.border}`,
      }}
    >
      {/* Navigation Items */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', py: 1.5 }}>
        <List component="nav" disablePadding>
          {filteredNav.map((item) => (
            <SidebarItem 
              key={item.id} 
              item={item} 
              collapsed={collapsed} 
              expandedId={expandedId}
              onToggle={handleToggle}
            />
          ))}
        </List>
      </Box>

      {/* Sidebar Footer with Logo & Powered By - fixed at bottom */}
      <Box
        sx={{
          py: 1.5,
          px: 1.5,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          borderTop: `1px solid ${tokens.colors.border}`,
          flexShrink: 0,
        }}
      >
        <Box
          component="img"
          src="/shreeji-logo-clean.png"
          alt="ShreeJi Logo"
          sx={{
            width: collapsed ? 36 : 145,
            height: collapsed ? 36 : 'auto',
            maxHeight: 56,
            objectFit: 'contain',
            transition: 'all 0.3s ease',
          }}
        />
        {!collapsed && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: '0.73rem',
              fontWeight: 600,
              letterSpacing: 0.3,
              whiteSpace: 'nowrap',
            }}
          >
            Powered By Harivansh Digital
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Mobile: Temporary drawer
  if (isMobileOrTablet) {
    return (
      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: tokens.layout.sidebar.expandedWidth,
            boxSizing: 'border-box',
            border: 'none',
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  // Desktop: Persistent sidebar
  return (
    <Box
      component="nav"
      sx={{
        width,
        flexShrink: 0,
        transition: `width ${tokens.transitions.normal}`,
      }}
    >
      <Box
        sx={{
          width,
          height: `calc(100vh - ${tokens.layout.topbar.height}px)`,
          position: 'fixed',
          top: `${tokens.layout.topbar.height}px`,
          left: 0,
          transition: `width ${tokens.transitions.normal}`,
          zIndex: tokens.zIndex.sidebar,
        }}
      >
        {sidebarContent}
      </Box>
    </Box>
  );
}
